<template>
    <BlockUI :blocked="isUploading">
        <div class="title-and-controls">
            <Title>Documents</Title>
            <FileAdd
                v-if="isEditing"
                label="Add document"
                header="Add document"
                size="small"
                :entity="entity"
                :navigateTo="navigateTo"
                :showToastOnSave="true"
                :artefacts="artefacts"
            />
        </div>
        <div
            class="documents"
            @dragover.prevent
            @dragenter="(e) => isEditing && toggleDrag(true, e)"
        >
            <div v-if="!artefacts.length" class="empty-state">
                <i class="pi pi-file empty-icon"></i>
                <div>
                    <h3>No documents</h3>
                    <p><i>Here you can add any kind of document.</i></p>
                </div>
                <FileAdd
                    v-if="isEditing"
                    label="Add document"
                    header="Add document"
                    size="small"
                    :entity="entity"
                    :navigateTo="navigateTo"
                    :showToastOnSave="true"
                    :artefacts="artefacts"
                    severity="primary"
                />
            </div>

            <DataTable
                :value="artefacts"
                v-if="artefacts.length"
                selectionMode="single"
                @row-click="
                    (event: DataTableRowClickEvent) => {
                        // Forward the click to the router link inside.
                        (event.originalEvent.target as HTMLElement)
                            .querySelector('a')
                            ?.click();
                    }
                "
            >
                <template #header>
                    <div class="title-and-controls">
                        <FileAdd
                            v-if="isEditing"
                            label="Add document"
                            header="Add document"
                            size="small"
                            :entity="entity"
                            :navigateTo="navigateTo"
                            :showToastOnSave="true"
                            :artefacts="artefacts"
                        />
                    </div>
                </template>

                <Column field="name" header="Name">
                    <template #body="{ data, field }">
                        <RouterLink
                            class="document-link"
                            :to="{
                                name: navigateTo,
                                params: {
                                    ...route.params,
                                    artefactId: data.identity,
                                },
                            }"
                        >
                            <div class="document-row">
                                <div
                                    class="document-icon"
                                    v-html="getIconForMimetype(data.mimetype)"
                                ></div>
                                {{ data[field] }}
                            </div>
                        </RouterLink>
                    </template>
                </Column>
            </DataTable>
            <div
                v-if="isDragging && isEditing"
                class="dropzone"
                @dragover.prevent
                @drop.prevent="(e) => uploadFiles(e.dataTransfer?.files)"
                @dragleave="(e) => toggleDrag(false, e)"
            >
                <h2>Drop your files to add</h2>
            </div>
        </div>
    </BlockUI>
</template>

<script setup lang="ts">
import { getIcon } from "material-file-icons";
import type { DataTableRowClickEvent } from "primevue/datatable";
import { ref, toRef } from "vue";
import { useRoute } from "vue-router";

import FileAdd from "@/components/FileAdd.vue";
import Title from "@/components/Title.vue";
import type { Domain } from "@/composables/domain-manager";
import type { Process } from "@/composables/process-manager";
import type { Product } from "@/composables/product-manager";
import type { Role } from "@/composables/role-manager";
import type { Stek } from "@/composables/stek-manager";
import { useUniqueArtefactManager } from "@/composables/unique-artefact-manager";
import type { ArtefactMeta } from "@/lib/artefacts";
import { useArtefactsStore } from "@/lib/artefacts/user";
import type { Task } from "@/stores/task-store";
import { mime } from "@/utils";

const route = useRoute();

const navigateTo = "documentPage";
const props = defineProps<{
    isEditing: boolean;
    entityType:
        | "stek"
        | "domain"
        | "product"
        | "process"
        | "task"
        | "role"
        | "gateway"
        | "event"
        | "flow";
    entity: Stek | Domain | Product | Process | Task | Role;
    artefacts: ArtefactMeta[];
}>();

const { isUploading, checkConflictsAndAddFiles } = useUniqueArtefactManager(
    toRef(() => props.entity.user_artefact_group_id || ""),
    toRef(() => props.artefacts || [])
);
const { updateArtefacts } = useArtefactsStore();

const isDragging = ref(false);

const toggleDrag = (toggle: boolean, event: DragEvent) => {
    event.preventDefault();
    isDragging.value = toggle;
};

const uploadFiles = async (files?: FileList | null) => {
    isDragging.value = false;
    if (!files || !files.length) {
        return;
    }
    await checkConflictsAndAddFiles(files, true, updateArtefacts);
};

const getIconForMimetype = (mimetype: string) => {
    if (mimetype.startsWith("application/vnd.google-apps")) {
        return '<i class="pi pi-google"></i>';
    }
    const filename = `.${mime.getExtension(mimetype)}`;
    return getIcon(filename).svg;
};
</script>

<style scoped lang="scss">
.title-and-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;

    > * {
        margin: 0;
    }

    .empty-icon {
        font-size: 3rem;
    }
}

a.document-link,
a.task-link {
    text-decoration: none;
}

.document-row {
    display: flex;
    gap: 0.2rem;
    align-items: center;
}
.document-icon {
    height: 1.5rem;
    width: 1.5rem;
    text-align: center;

    :deep(> *) {
        margin: auto;
    }

    :deep(> .pi) {
        height: 1.5rem;
        line-height: 1.5rem;
    }
}

.documents {
    position: relative;

    .dropzone {
        z-index: 1;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex: 1;
        justify-content: center;
        align-items: center;
        background: rgba(255, 255, 255, 0.75);
        outline: 2px dashed var(--stekz-primary-color);

        * {
            pointer-events: none;
        }
    }
}
</style>
